import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import {
  adminGetNowPaymentsConfig,
  adminUpdateNowPaymentsConfig,
  adminTestNowPayments,
  getCryptoCurrencies,
} from '../../lib/api';
import AdminSidebar from '../../components/AdminSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import {
  Bitcoin, Save, CheckCircle2, AlertCircle, Loader2, Eye, EyeOff,
  Wallet, FlaskConical, ShieldCheck, ArrowRightLeft, Coins, Info,
} from 'lucide-react';
import { toast } from 'sonner';

const CRYPTO_OPTIONS = [
  { code: 'btc',         label: 'Bitcoin',           symbol: 'BTC',   network: 'Bitcoin' },
  { code: 'eth',         label: 'Ethereum',          symbol: 'ETH',   network: 'Ethereum' },
  { code: 'usdttrc20',   label: 'Tether',            symbol: 'USDT',  network: 'TRC20 (Tron)' },
  { code: 'usdterc20',   label: 'Tether',            symbol: 'USDT',  network: 'ERC20 (Ethereum)' },
  { code: 'usdc',        label: 'USD Coin',          symbol: 'USDC',  network: 'Ethereum' },
  { code: 'sol',         label: 'Solana',            symbol: 'SOL',   network: 'Solana' },
  { code: 'bnbbsc',      label: 'BNB',               symbol: 'BNB',   network: 'BSC' },
  { code: 'ltc',         label: 'Litecoin',          symbol: 'LTC',   network: 'Litecoin' },
  { code: 'doge',        label: 'Dogecoin',          symbol: 'DOGE',  network: 'Dogecoin' },
  { code: 'xrp',         label: 'XRP',               symbol: 'XRP',   network: 'XRP Ledger' },
  { code: 'matic',       label: 'Polygon',           symbol: 'MATIC', network: 'Polygon' },
  { code: 'trx',         label: 'Tron',              symbol: 'TRX',   network: 'Tron' },
  { code: 'dai',         label: 'DAI',               symbol: 'DAI',   network: 'Ethereum' },
  { code: 'avax',        label: 'Avalanche',         symbol: 'AVAX',  network: 'Avalanche' },
  { code: 'shib',        label: 'Shiba Inu',         symbol: 'SHIB',  network: 'Ethereum' },
];

export default function AdminCryptoPayments() {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showSecrets, setShowSecrets] = useState({ api_key: false, ipn_secret: false });
  const [liveCurrencies, setLiveCurrencies] = useState([]);

  useEffect(() => {
    if (loading) return;
    if (!user || !isAdmin) { navigate('/admin/login'); return; }
    loadConfig();
    loadLiveCurrencies();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin, loading]);

  const loadConfig = async () => {
    try {
      const res = await adminGetNowPaymentsConfig();
      setCfg(res.data);
      setDraft({
        enabled: res.data.enabled || false,
        mode: res.data.mode || 'live',
        api_key: '',
        ipn_secret: '',
        public_key: res.data.public_key || '',
        payout_address: res.data.payout_address || '',
        payout_currency: res.data.payout_currency || 'usdttrc20',
        enabled_currencies: res.data.enabled_currencies || [],
        min_amount_gbp: res.data.min_amount_gbp ?? 20,
        is_fee_paid_by_user: res.data.is_fee_paid_by_user !== false,
        use_custody: res.data.use_custody !== false,
      });
    } catch {
      toast.error('Erreur de chargement');
    }
  };

  const loadLiveCurrencies = async () => {
    try {
      const res = await getCryptoCurrencies();
      setLiveCurrencies(res.data?.currencies || []);
    } catch {
      /* ignore */
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...draft };
      // Don't send empty secrets — preserve existing
      if (!payload.api_key) delete payload.api_key;
      if (!payload.ipn_secret) delete payload.ipn_secret;
      await adminUpdateNowPaymentsConfig(payload);
      toast.success('Configuration sauvegardée');
      await loadConfig();
      await loadLiveCurrencies();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await adminTestNowPayments();
      setTestResult({ ok: true, ...res.data });
      toast.success('Connexion NOWPayments OK ✓');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Connexion échouée';
      setTestResult({ ok: false, error: msg });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  const toggleCurrency = (code) => {
    const exists = draft.enabled_currencies?.includes(code);
    setDraft({
      ...draft,
      enabled_currencies: exists
        ? draft.enabled_currencies.filter((c) => c !== code)
        : [...(draft.enabled_currencies || []), code],
    });
  };

  if (loading || !cfg) {
    return (
      <div className="min-h-screen flex bg-cream">
        <AdminSidebar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-10 h-10 animate-spin text-gold" />
        </div>
      </div>
    );
  }

  const ipnCallbackUrl = `${window.location.origin}/api/crypto/ipn`;
  const trc20Valid = (draft.payout_address || '').startsWith('T') && (draft.payout_address || '').length === 34;

  return (
    <div className="min-h-screen flex bg-cream">
      <AdminSidebar />
      <main className="flex-1 p-6 lg:p-10 overflow-x-hidden">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-8 flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="font-display text-3xl font-bold text-rolex-green mb-1 flex items-center gap-3">
                <Bitcoin className="w-8 h-8 text-gold-dark" />
                Paiements Crypto (NOWPayments)
              </h1>
              <p className="text-rolex-green/65 text-sm">
                Le client paye en BTC, ETH, USDT, SOL… → conversion automatique en USDT TRC20 → envoi direct sur votre Trust Wallet.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={cfg.enabled ? 'bg-emerald-500 text-white px-3 py-1.5' : 'bg-stone-300 text-stone-700 px-3 py-1.5'}>
                {cfg.enabled ? '🟢 ACTIF' : '⚫ Désactivé'}
              </Badge>
              <Badge className={cfg.mode === 'sandbox' ? 'bg-amber-500 text-white' : 'bg-emerald-600 text-white'}>
                {cfg.mode === 'sandbox' ? '🧪 Sandbox' : '🔴 Live'}
              </Badge>
            </div>
          </div>

          {/* Activation + Trust Wallet */}
          <Card className="mb-6 border-2 border-gold/30 bg-white">
            <CardHeader>
              <CardTitle className="text-rolex-green flex items-center gap-2">
                <Wallet className="w-5 h-5 text-gold-dark" /> Trust Wallet & Activation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="flex items-center justify-between p-4 rounded-xl bg-cream border border-gold/20">
                <div>
                  <Label className="text-rolex-green font-bold">Activer les paiements crypto</Label>
                  <p className="text-xs text-rolex-green/60 mt-1">Le bouton "Payer en crypto" apparaîtra sur le site public.</p>
                </div>
                <Switch
                  checked={draft.enabled}
                  onCheckedChange={(v) => setDraft({ ...draft, enabled: v })}
                  data-testid="nowpayments-enabled-switch"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-rolex-green font-bold flex items-center gap-2">
                  <ArrowRightLeft className="w-4 h-4 text-emerald-600" />
                  Votre adresse Trust Wallet USDT TRC20
                </Label>
                <Input
                  value={draft.payout_address || ''}
                  onChange={(e) => setDraft({ ...draft, payout_address: e.target.value.trim() })}
                  placeholder="T... (adresse Tron commençant par T)"
                  className={`font-mono text-sm border-2 ${trc20Valid ? 'border-emerald-400' : (draft.payout_address ? 'border-red-300' : 'border-gold/30')}`}
                  data-testid="nowpayments-payout-address"
                />
                <div className="flex items-center gap-2 text-xs">
                  {trc20Valid ? (
                    <span className="text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Adresse TRC20 valide</span>
                  ) : draft.payout_address ? (
                    <span className="text-red-700 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Doit commencer par "T" et faire 34 caractères</span>
                  ) : (
                    <span className="text-rolex-green/55 italic">Adresse Tron USDT TRC20 où tous les paiements seront envoyés automatiquement</span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Monnaie de payout</Label>
                  <select
                    value={draft.payout_currency || 'usdttrc20'}
                    onChange={(e) => setDraft({ ...draft, payout_currency: e.target.value })}
                    className="w-full p-2 border border-gold/30 rounded-md bg-white font-mono text-sm h-10"
                  >
                    <option value="usdttrc20">USDT TRC20 (recommandé)</option>
                    <option value="usdterc20">USDT ERC20 (Ethereum)</option>
                    <option value="usdc">USDC</option>
                    <option value="btc">Bitcoin</option>
                    <option value="eth">Ethereum</option>
                  </select>
                  <p className="text-[10px] text-rolex-green/55 italic">Format dans lequel vous recevrez TOUS les paiements (conversion auto).</p>
                </div>
                <div className="space-y-2">
                  <Label>Montant minimum (£)</Label>
                  <Input
                    type="number"
                    value={draft.min_amount_gbp ?? 20}
                    onChange={(e) => setDraft({ ...draft, min_amount_gbp: parseFloat(e.target.value) || 20 })}
                    className="font-mono"
                  />
                  <p className="text-[10px] text-rolex-green/55 italic">⚠️ NOWPayments charge ~10 USDT fixe par payout. Min 20£ recommandé.</p>
                </div>
              </div>

              {/* Critical fees-paid-by-user toggle */}
              <div className={`flex items-start gap-3 p-4 rounded-xl border-2 ${draft.is_fee_paid_by_user ? 'bg-emerald-50 border-emerald-300' : 'bg-red-50 border-red-300'}`}>
                <Switch
                  checked={draft.is_fee_paid_by_user !== false}
                  onCheckedChange={(v) => setDraft({ ...draft, is_fee_paid_by_user: v })}
                  data-testid="fees-paid-by-user-switch"
                />
                <div className="flex-1">
                  <Label className="font-bold text-rolex-green">
                    💰 Les frais réseau sont payés par le CLIENT (recommandé)
                  </Label>
                  <p className={`text-xs mt-1.5 ${draft.is_fee_paid_by_user ? 'text-emerald-800' : 'text-red-800 font-bold'}`}>
                    {draft.is_fee_paid_by_user ? (
                      <>✅ Sur un produit à 10£ (~13 USDT), le client devra envoyer ~23 USDT (13 + frais réseau ~10). Vous recevez exactement les 13 USDT.</>
                    ) : (
                      <>🚨 <strong>DANGER</strong>: VOUS payez les frais réseau ~10 USDT à chaque transaction. Sur 13 USDT vous ne recevrez que ~3 USDT (perte de 75% sur les petits montants).</>
                    )}
                  </p>
                  <p className="text-[10px] text-rolex-green/55 italic mt-1">
                    NOWPayments paramètre <code>is_fee_paid_by_user</code>. Affecte tous les paiements crypto futurs.
                  </p>
                </div>
              </div>

              {/* Critical Custody info (cannot be disabled per NOWPayments support) */}
              <div className="flex items-start gap-3 p-4 rounded-xl border-2 border-blue-300 bg-blue-50">
                <Switch
                  checked={!draft.use_custody}
                  onCheckedChange={(v) => setDraft({ ...draft, use_custody: !v })}
                  data-testid="direct-payout-switch"
                />
                <div className="flex-1">
                  <Label className="font-bold text-rolex-green">
                    ⚠️ Envoi direct chaque vente (NE PAS ACTIVER)
                  </Label>
                  <div className={`mt-2 p-3 rounded-lg border-2 ${draft.use_custody ? 'bg-emerald-50 border-emerald-300' : 'bg-red-50 border-red-400'}`}>
                    <p className="text-xs font-bold mb-1">
                      {draft.use_custody ? (
                        <span className="text-emerald-800">✅ État actuel : DÉSACTIVÉ — économies optimales activées</span>
                      ) : (
                        <span className="text-red-800">🚨 État actuel : ACTIVÉ — vous payez ~10 USDT par transaction !</span>
                      )}
                    </p>
                    <p className="text-[11px] text-rolex-green/85">
                      {draft.use_custody ? (
                        <>Les paiements crypto s'accumulent dans votre balance custody NOWPayments. Vous retirez UNE fois par semaine = ~95% d'économies. C'est la bonne configuration.</>
                      ) : (
                        <>Chaque vente déclenche un envoi immédiat sur Trust Wallet avec frais réseau ~10 USDT. Désactivez ce toggle au plus vite !</>
                      )}
                    </p>
                  </div>
                  <div className="mt-3 p-3 rounded bg-white/80 text-[11px] text-blue-900 leading-relaxed border border-blue-200">
                    <p className="font-bold mb-2 text-blue-900 text-xs">🔁 Votre workflow (à suivre 1× par semaine) :</p>
                    <ol className="list-decimal list-inside space-y-1 ml-1">
                      <li><strong>Lundi au dimanche</strong> : les ventes crypto s'accumulent automatiquement dans votre balance NOWPayments (Trust Wallet reste à 0, c'est normal)</li>
                      <li><strong>Lundi matin</strong> : connectez-vous sur <a href="https://account.nowpayments.io" target="_blank" rel="noopener noreferrer" className="underline">account.nowpayments.io</a></li>
                      <li>Menu "Balance" ou "Wallets" → vous voyez la somme accumulée (ex: 2,500 USDT)</li>
                      <li>Cliquez <strong>"Withdraw"</strong> → USDT TRC20 → adresse Trust Wallet ({(draft.payout_address || '').substring(0, 8)}…)</li>
                      <li>Vous payez ~5.66 USDT de frais UNE seule fois pour TOUTES les ventes accumulées</li>
                    </ol>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* API Credentials */}
          <Card className="mb-6 border-2 border-gold/30 bg-white">
            <CardHeader>
              <CardTitle className="text-rolex-green flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-gold-dark" /> Clés API NOWPayments
              </CardTitle>
              <p className="text-xs text-rolex-green/65 mt-2">
                Récupérez vos clés sur <a href="https://account.nowpayments.io/store-settings" target="_blank" rel="noopener noreferrer" className="underline font-semibold text-gold-dark">account.nowpayments.io → Store Settings</a>
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="flex items-center justify-between">
                  <span>Clé API (x-api-key)</span>
                  {cfg.api_key_set && <Badge className="bg-emerald-500/20 text-emerald-700 border-emerald-300 text-[10px]">Configurée ✓</Badge>}
                </Label>
                <div className="relative">
                  <Input
                    type={showSecrets.api_key ? 'text' : 'password'}
                    value={draft.api_key || ''}
                    onChange={(e) => setDraft({ ...draft, api_key: e.target.value })}
                    placeholder={cfg.api_key_set ? '•••••••• (laissez vide pour ne pas changer)' : 'XXXXX-XXXXX-XXXXX-XXXXX'}
                    className="pr-10 font-mono text-sm border-gold/30"
                    data-testid="nowpayments-api-key"
                  />
                  <button type="button" onClick={() => setShowSecrets({ ...showSecrets, api_key: !showSecrets.api_key })} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-rolex-green/55 hover:text-rolex-green" tabIndex={-1}>
                    {showSecrets.api_key ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center justify-between">
                  <span>IPN Secret (pour valider les webhooks)</span>
                  {cfg.ipn_secret_set && <Badge className="bg-emerald-500/20 text-emerald-700 border-emerald-300 text-[10px]">Configuré ✓</Badge>}
                </Label>
                <div className="relative">
                  <Input
                    type={showSecrets.ipn_secret ? 'text' : 'password'}
                    value={draft.ipn_secret || ''}
                    onChange={(e) => setDraft({ ...draft, ipn_secret: e.target.value })}
                    placeholder={cfg.ipn_secret_set ? '•••••••• (laissez vide pour ne pas changer)' : 'IPN secret depuis NOWPayments'}
                    className="pr-10 font-mono text-sm border-gold/30"
                    data-testid="nowpayments-ipn-secret"
                  />
                  <button type="button" onClick={() => setShowSecrets({ ...showSecrets, ipn_secret: !showSecrets.ipn_secret })} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-rolex-green/55 hover:text-rolex-green" tabIndex={-1}>
                    {showSecrets.ipn_secret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Public Key (facultatif, utilisé par certains intégrations)</Label>
                <Input
                  value={draft.public_key || ''}
                  onChange={(e) => setDraft({ ...draft, public_key: e.target.value })}
                  placeholder="UUID public key"
                  className="font-mono text-sm border-gold/30"
                />
              </div>
            </CardContent>
          </Card>

          {/* Webhook info */}
          <Card className="mb-6 border-2 border-gold/20 bg-cream">
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-gold-dark shrink-0 mt-0.5" />
                <div className="text-sm text-rolex-green/85 space-y-2">
                  <p className="font-bold">📡 Configuration du Webhook IPN NOWPayments</p>
                  <p>
                    Pour que vous receviez automatiquement la confirmation des paiements, ajoutez ce callback dans NOWPayments :
                  </p>
                  <code className="block bg-white p-2 rounded border border-gold/20 font-mono text-xs break-all">
                    {ipnCallbackUrl}
                  </code>
                  <p>
                    👉 <a href="https://account.nowpayments.io/store-settings/instant-payment-notifications" target="_blank" rel="noopener noreferrer" className="text-gold-dark hover:underline font-semibold">Configurer dans NOWPayments → IPN Settings</a>
                  </p>
                  <p className="text-xs text-rolex-green/55">
                    Le code valide automatiquement la signature HMAC-SHA512 avec votre IPN secret.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Currencies */}
          <Card className="mb-6 border-2 border-gold/30 bg-white">
            <CardHeader>
              <CardTitle className="text-rolex-green flex items-center gap-2">
                <Coins className="w-5 h-5 text-gold-dark" /> Cryptomonnaies acceptées
              </CardTitle>
              <p className="text-xs text-rolex-green/65 mt-2">Sélectionnez les cryptos visibles dans le checkout. {liveCurrencies.length > 0 && <span className="text-emerald-700">✓ {liveCurrencies.length} actuellement disponibles via votre API.</span>}</p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {CRYPTO_OPTIONS.map((c) => {
                  const active = draft.enabled_currencies?.includes(c.code);
                  const liveInfo = liveCurrencies.find((lc) => lc.currency === c.code);
                  return (
                    <button
                      key={c.code}
                      type="button"
                      onClick={() => toggleCurrency(c.code)}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${active ? 'border-emerald-500 bg-emerald-50 shadow-md' : 'border-stone-200 bg-white hover:border-gold/40'}`}
                      data-testid={`crypto-toggle-${c.code}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-bold text-rolex-green text-sm">{c.symbol}</p>
                        {active && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                      </div>
                      <p className="text-[11px] text-rolex-green/75 truncate">{c.label}</p>
                      <p className="text-[9px] text-rolex-green/55">{c.network}</p>
                      {liveInfo && (
                        <p className="text-[9px] font-mono text-emerald-600 mt-0.5">min: {liveInfo.min_amount}</p>
                      )}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex flex-wrap gap-3 items-center justify-between">
            <Button
              variant="outline"
              onClick={handleTest}
              disabled={testing || saving}
              className="border-gold/40 text-gold-dark hover:bg-gold/10 gap-2"
              data-testid="test-nowpayments-btn"
            >
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
              Tester la connexion
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="btn-gold gap-2 px-8 py-6 text-base"
              data-testid="save-nowpayments-config"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-5 h-5" />}
              Sauvegarder
            </Button>
          </div>

          {testResult && (
            <Card className={`mt-4 border-2 ${testResult.ok ? 'border-emerald-300 bg-emerald-50' : 'border-red-300 bg-red-50'}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  {testResult.ok ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" /> : <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />}
                  <div className="text-sm">
                    {testResult.ok ? (
                      <>
                        <p className="font-bold text-emerald-900">✅ NOWPayments connecté</p>
                        <p className="text-emerald-800 text-xs">Message: {testResult.message}</p>
                      </>
                    ) : (
                      <>
                        <p className="font-bold text-red-900">❌ Échec de la connexion</p>
                        <p className="text-red-800 text-xs">{testResult.error}</p>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
